import {
  SECTION_ORDER,
  SectionKeyType,
  SectionStatus,
  SectionStatusType
} from './data-qa-constants';
import { LoadResult, MmmConfig, PriorApproval, SectionDiffs } from './data-qa-types';
import { buildEmptyNotesRecord, buildEmptyStatusRecord } from './data-qa-utils';

export interface DataQaState {
  status: 'idle' | 'loading' | 'ready' | 'error';
  errorMessage?: string;
  config?: MmmConfig;
  dataset?: LoadResult['dataset'];
  priorApproval: PriorApproval | null;
  diffsBySection: Record<SectionKeyType, SectionDiffs>;
  sectionStatus: Record<SectionKeyType, SectionStatusType>;
  sectionNotes: Record<SectionKeyType, string>;
  // Sections whose current sectionStatus/sectionNotes were auto-inherited from
  // another model's review this session (see the auto-inherit effect in
  // DataQA.tsx), not a genuine decision made ON this model. An inherited copy must
  // never itself become a "source" another model attributes to -- without this,
  // once two models both carry the same inherited status, useSharedSectionReview
  // can no longer tell who the real origin was, and picks whichever happens to sit
  // first in array order, producing contradictory attribution (confirmed live on
  // away's DTC/Commerce/Brand Equity Portfolio banners, 2026-08-27).
  inheritedSections: Partial<Record<SectionKeyType, boolean>>;
  pendingApproval: Record<SectionKeyType, boolean>;
  finalized: boolean;
}

export const initialDataQaState: DataQaState = {
  status: 'idle',
  priorApproval: null,
  diffsBySection: {} as Record<SectionKeyType, SectionDiffs>,
  sectionStatus: buildEmptyStatusRecord(),
  sectionNotes: buildEmptyNotesRecord(),
  inheritedSections: {},
  pendingApproval: SECTION_ORDER.reduce(
    (acc, key) => {
      acc[key] = false;
      return acc;
    },
    {} as Record<SectionKeyType, boolean>
  ),
  finalized: false
};

export type DataQaAction =
  | { type: 'LOAD_START' }
  | { type: 'LOAD_SUCCESS'; result: LoadResult }
  | { type: 'LOAD_ERROR'; message: string }
  | { type: 'SET_NOTE'; section: SectionKeyType; note: string }
  | { type: 'APPROVAL_PENDING'; section: SectionKeyType; pending: boolean }
  | {
      type: 'APPROVAL_SUCCESS';
      section: SectionKeyType;
      status: SectionStatusType;
      notes?: string;
      // Set by the auto-inherit effect specifically -- see inheritedSections above.
      // Omitted (falsy) for every genuine handleApprove/handleFlag dispatch.
      inherited?: boolean;
    }
  | { type: 'FINALIZED' };

export const dataQaReducer = (state: DataQaState, action: DataQaAction): DataQaState => {
  switch (action.type) {
    case 'LOAD_START':
      return { ...initialDataQaState, status: 'loading' };
    case 'LOAD_SUCCESS': {
      const { result } = action;
      const sectionStatus = result.priorApproval
        ? { ...result.priorApproval.sectionStatus }
        : buildEmptyStatusRecord();
      const sectionNotes = result.priorApproval
        ? { ...result.priorApproval.sectionNotes }
        : buildEmptyNotesRecord();
      // Only reset inheritedSections on a GENUINE model switch (config identity
      // actually changes) -- usePrefetchOtherModels warms other models' queries, so
      // switching to an already-cached model resolves synchronously and dispatches
      // LOAD_SUCCESS directly (no intervening LOAD_START, the only other action that
      // resets this); without resetting here too, a section auto-inherited on the
      // PREVIOUS model stayed flagged as inherited on the new model even when the new
      // model has its own genuine result.priorApproval for that section. But
      // persistApproval's own queryClient.setQueryData (see DataQA.tsx) re-triggers
      // this SAME action for the SAME model whenever ANY approval for it persists --
      // including the auto-inherit effect's own APPROVAL_SUCCESS{inherited:true},
      // which already set state.inheritedSections correctly moments earlier.
      // Resetting unconditionally wiped that flag right back to false on the very
      // next render, reproducing the exact contradictory-attribution bug this field
      // exists to prevent. Preserve state.inheritedSections whenever the config
      // hasn't actually changed.
      const configChanged =
        !state.config ||
        state.config.configPath !== result.config.configPath ||
        state.config.configHash !== result.config.configHash;
      return {
        ...state,
        status: 'ready',
        errorMessage: undefined,
        config: result.config,
        dataset: result.dataset,
        priorApproval: result.priorApproval,
        diffsBySection: result.diffsBySection,
        sectionStatus,
        sectionNotes,
        inheritedSections: configChanged ? {} : state.inheritedSections,
        finalized: false
      };
    }
    case 'LOAD_ERROR':
      return { ...initialDataQaState, status: 'error', errorMessage: action.message };
    case 'SET_NOTE':
      return {
        ...state,
        sectionNotes: { ...state.sectionNotes, [action.section]: action.note }
      };
    case 'APPROVAL_PENDING':
      return {
        ...state,
        pendingApproval: { ...state.pendingApproval, [action.section]: action.pending }
      };
    case 'APPROVAL_SUCCESS': {
      const notes =
        action.status === SectionStatus.APPROVED
          ? ''
          : (action.notes ?? state.sectionNotes[action.section]);
      return {
        ...state,
        sectionStatus: { ...state.sectionStatus, [action.section]: action.status },
        sectionNotes: { ...state.sectionNotes, [action.section]: notes },
        inheritedSections: {
          ...state.inheritedSections,
          [action.section]: Boolean(action.inherited)
        },
        pendingApproval: { ...state.pendingApproval, [action.section]: false }
      };
    }
    case 'FINALIZED':
      return { ...state, finalized: true };
    default:
      return state;
  }
};
